import { useState, type ChangeEvent } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { Checkbox } from '@components/checkbox/Checkbox';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { REPEAT_OPTIONS_DEFAULT } from '../bulletins/constants';
import styles from './SelectPeaksDaysModal.scss';

interface SelectPeaksDaysModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (selectedDays: number[]) => void;
  selectedDay: number;
}

export const SelectPeaksDaysModal = ({
  isOpen,
  selectedDay,
  onClose,
  onCreate,
}: SelectPeaksDaysModalProps) => {
  const daysOfWeek = REPEAT_OPTIONS_DEFAULT.filter(
    (day) => day.value !== '-1' && day.value !== selectedDay.toString(),
  );
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const allDayValues = daysOfWeek.map((day) => Number(day.value));
  const allSelected = allDayValues.every((value) =>
    selectedDays.includes(value),
  );

  const handleToggleDay = (dayIndex: number) => (_e: ChangeEvent) => {
    setSelectedDays((prev) => {
      const next = [...prev];
      if (next.includes(dayIndex)) {
        return next.filter((day) => day !== dayIndex);
      } else {
        return [...next, dayIndex];
      }
    });
  };

  const handleCreate = () => {
    onCreate(Array.from(selectedDays).sort());
    setSelectedDays([]);
  };

  const handleClose = () => {
    setSelectedDays([]);
    onClose();
  };

  const handleSelectAll = () => {
    setSelectedDays(allSelected ? [] : allDayValues);
  };

  if (!isOpen) return null;

  return (
    <Modal
      title="Select Days of the Week"
      isOpen={isOpen}
      onClose={handleClose}
      subtitle="Select the days you want to copy busy hours to:"
    >
      <div className={styles.daysGrid}>
        {daysOfWeek.map((day) => (
          <div className={styles.day} key={day.value}>
            <Checkbox
              key={day.value}
              label={''}
              checked={selectedDays.includes(Number(day.value))}
              onChange={handleToggleDay(Number(day.value))}
            />
            <span>{day.label}</span>
          </div>
        ))}
      </div>
      <button
        type="button"
        className={styles.selectAllButton}
        onClick={handleSelectAll}
      >
        {allSelected ? 'Deselect all' : 'Select all'}
      </button>
      <ModalActions className={styles.actions}>
        <Button
          label="Cancel"
          onClick={handleClose}
          variant={ButtonVariants.Tertiary}
        />
        <Button
          label={'Apply'}
          onClick={handleCreate}
          variant={ButtonVariants.Primary}
          isDisabled={selectedDays.length === 0}
        />
      </ModalActions>
    </Modal>
  );
};
